import { SDM_GRID_COLS, SDM_GRID_ROWS } from './gridPositions';

interface Identifiable {
  id?: string;
}

export function findRowIndex<T extends Identifiable>(
  widgetId: string,
  rows: readonly T[][],
): number {
  return rows.findIndex((row) => row.some((w) => w.id === widgetId));
}

/**
 * Where a dragged widget would land. Shared by the insertion bar during drag
 * and the committed grouping on drop so the two cannot disagree. Layout stays
 * untouched until drop.
 *
 * - `orientation: 'vertical'` — insert within row `rowIndex` at slot
 *   `insertIndex` (0..row.length).
 * - `orientation: 'horizontal'` — insert as a new single-widget row at
 *   `rowIndex` (0..rows.length). `insertIndex` is ignored.
 */
export interface InsertionPoint {
  orientation: 'vertical' | 'horizontal';
  rowIndex: number;
  insertIndex: number;
}

export function insertionPointsEqual(a: InsertionPoint | null, b: InsertionPoint | null): boolean {
  return (
    a === b ||
    (a != null &&
      b != null &&
      a.orientation === b.orientation &&
      a.rowIndex === b.rowIndex &&
      a.insertIndex === b.insertIndex)
  );
}

function sameArrangement<T extends Identifiable>(a: readonly T[][], b: readonly T[][]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i].length !== b[i].length) return false;
    for (let j = 0; j < a[i].length; j++) {
      if (a[i][j].id !== b[i][j].id) return false;
    }
  }
  return true;
}

export interface PointerPosition {
  x: number;
  y: number;
}

/**
 * Maps the cursor to an `InsertionPoint`, or null when it is not close enough
 * to any insertion location. Drag geometry — not dnd-kit collision/sort —
 * drives the bar so widgets never shift or resize mid-drag.
 *
 * Targets, each proximity-gated by `bufferPx`:
 * - New row: cursor Y near a row seam (only when `canAddRow`).
 * - Within row: cursor X near a slot boundary in the hovered row.
 * When both are in range, the closer one wins.
 */
export function pointerToInsertionPoint<T extends { position: { w: number } }>(
  rows: readonly T[][],
  pointer: PointerPosition,
  widthPx: number,
  heightPx: number,
  canAddRow: boolean,
  bufferPx: number,
): InsertionPoint | null {
  if (rows.length === 0 || widthPx <= 0 || heightPx <= 0) return null;

  const { x: cursorX, y: cursorY } = pointer;
  const rowHeightPx = heightPx / rows.length;

  let horizontalDist = Infinity;
  let horizontalSeam = 0;
  if (canAddRow) {
    for (let seam = 0; seam <= rows.length; seam++) {
      const dist = Math.abs(cursorY - seam * rowHeightPx);
      if (dist < horizontalDist) {
        horizontalDist = dist;
        horizontalSeam = seam;
      }
    }
  }

  const rowIndex = Math.max(0, Math.min(Math.floor(cursorY / rowHeightPx), rows.length - 1));
  const row = rows[rowIndex];
  let verticalDist = Math.abs(cursorX);
  let verticalIndex = 0;
  let accumulated = 0;
  for (let i = 0; i < row.length; i++) {
    accumulated += (row[i].position.w / 100) * widthPx;
    const dist = Math.abs(accumulated - cursorX);
    if (dist < verticalDist) {
      verticalDist = dist;
      verticalIndex = i + 1;
    }
  }

  const horizontalInRange = horizontalDist <= bufferPx;
  const verticalInRange = verticalDist <= bufferPx;

  if (!horizontalInRange && !verticalInRange) return null;

  if (horizontalInRange && (!verticalInRange || horizontalDist < verticalDist)) {
    return { orientation: 'horizontal', rowIndex: horizontalSeam, insertIndex: 0 };
  }

  return { orientation: 'vertical', rowIndex, insertIndex: verticalIndex };
}

/**
 * Moves `activeId` to `point` and returns the resulting grouping, or null when
 * the move is invalid or a no-op. A cross-row insert into an already-full row is
 * rejected (swap covers that case) rather than cascading overflow. Also rejected
 * when the result would exceed SDM_GRID_ROWS or leave the arrangement unchanged.
 * Emptied rows are dropped.
 */
export function applyInsertionPoint<T extends Identifiable>(
  rows: readonly T[][],
  activeId: string,
  point: InsertionPoint,
): T[][] | null {
  const source = findRowIndex(activeId, rows);
  if (source === -1) return null;
  const widget = rows[source].find((w) => w.id === activeId);
  if (!widget) return null;

  let result: T[][];

  if (point.orientation === 'horizontal') {
    const withoutActive = rows.map((row, i) =>
      i === source ? row.filter((w) => w.id !== activeId) : [...row],
    );

    // `point.rowIndex` indexes the pre-removal row list; if the source row
    // empties and sits above the target, dropping it shifts the target up one.
    let position = point.rowIndex;
    if (withoutActive[source].length === 0 && source < position) position -= 1;

    const kept = withoutActive.filter((row) => row.length > 0);
    const clamped = Math.max(0, Math.min(position, kept.length));
    result = [...kept.slice(0, clamped), [widget], ...kept.slice(clamped)];
  } else {
    const dest = point.rowIndex;
    if (dest < 0 || dest >= rows.length) return null;

    const sourceRow = [...rows[source]];
    const removeIndex = sourceRow.findIndex((w) => w.id === activeId);
    sourceRow.splice(removeIndex, 1);

    if (dest === source) {
      // Removing the widget shifts every later slot in the same row left by one.
      let insertIndex = point.insertIndex;
      if (removeIndex < insertIndex) insertIndex -= 1;
      insertIndex = Math.max(0, Math.min(insertIndex, sourceRow.length));
      sourceRow.splice(insertIndex, 0, widget);
      result = rows.map((row, i) => (i === source ? sourceRow : [...row]));
    } else {
      const destRow = [...rows[dest]];
      // A full row can't take another widget — swap handles that case instead.
      if (destRow.length >= SDM_GRID_COLS) return null;
      const insertIndex = Math.max(0, Math.min(point.insertIndex, destRow.length));
      destRow.splice(insertIndex, 0, widget);
      result = rows
        .map((row, i) => {
          if (i === source) return sourceRow;
          if (i === dest) return destRow;
          return [...row];
        })
        .filter((row) => row.length > 0);
    }
  }

  if (result.length > SDM_GRID_ROWS) return null;
  if (result.some((row) => row.length > SDM_GRID_COLS)) return null;
  if (sameArrangement(result, rows)) return null;

  return result;
}

interface Positioned extends Identifiable {
  position: { w: number };
}

/**
 * The widget whose painted slot contains `pointer` (px, relative to the grid),
 * or null when the pointer is outside every widget. Drives swap mode: when the
 * cursor sits deep inside a widget — too far from any boundary to insert —
 * dropping trades places with that widget instead.
 *
 * Geometry matches Grid paint and `pointerToInsertionPoint`: equal-height flex
 * rows (`height / rowCount`), widgets laid out left-to-right by stored width
 * percentages. Stored `{x,y,h}` are ignored so legacy unequal heights cannot
 * disagree with the teal swap target under the cursor.
 */
export function pointerToWidget<T extends Positioned>(
  rows: readonly T[][],
  pointer: PointerPosition,
  widthPx: number,
  heightPx: number,
): T | null {
  if (rows.length === 0 || widthPx <= 0 || heightPx <= 0) return null;

  const rowHeightPx = heightPx / rows.length;
  const rowIndex = Math.floor(pointer.y / rowHeightPx);
  if (rowIndex < 0 || rowIndex >= rows.length) return null;

  const row = rows[rowIndex];
  let left = 0;
  for (const widget of row) {
    const right = left + (widget.position.w / 100) * widthPx;
    if (pointer.x >= left && pointer.x < right) {
      return widget;
    }
    left = right;
  }

  return null;
}

/**
 * Trades the grid slots of two widgets, returning the new grouping, or null when
 * the ids match or either is missing. Positions are recomputed downstream by
 * redistributeRows, so each widget takes over the other's slot geometry.
 */
export function swapWidgets<T extends Identifiable>(
  rows: readonly T[][],
  aId: string,
  bId: string,
): T[][] | null {
  if (aId === bId) return null;

  const aRow = findRowIndex(aId, rows);
  const bRow = findRowIndex(bId, rows);
  if (aRow === -1 || bRow === -1) return null;

  const aCol = rows[aRow].findIndex((w) => w.id === aId);
  const bCol = rows[bRow].findIndex((w) => w.id === bId);

  const result = rows.map((row) => [...row]);
  const temp = result[aRow][aCol];
  result[aRow][aCol] = result[bRow][bCol];
  result[bRow][bCol] = temp;
  return result;
}
